import { Box, Stack, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { externalAPIKeys } from "../../../../api/queryKeys";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import AppPage from "../../../../components/structure/AppPage";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { getPrettyDateTime, useLocalTime } from "../../../../utils/timestamp";
import { icons } from "../../../stack/environment/icons/iconExport";
import ExternalAPIKeyCreationModal from "./ExternalAPIKeyCreationModal";
import ExternalAPIKeyDisplayModal from "./ExternalAPIKeyDisplayModal";
import { getHealthCommand } from "./utils";

const ExternalAPIView = () => {
  const [apiKey, setApiKey] = useState("");
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);

  const {
    getBreezeBackendURL,
    generateExternalApiKey,
    getExternalApiKeys,
    trackEvent,
  } = useAPI();
  const colors = useColors();
  const showLocalTime = useLocalTime();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const { data: backendURL, isLoading: isLoadingBackendURL } = useQuery({
    queryKey: externalAPIKeys.backendURL(),
    queryFn: getBreezeBackendURL,
    select: (response) => response.data,
  });

  const { data: apiKeys, isLoading: isLoadingApiKeys } = useQuery({
    queryKey: externalAPIKeys.apiKeys(),
    queryFn: getExternalApiKeys,
    select: (response) => response.data,
  });

  const handleGenerateAPIKey = async (keyName, expiry_days) => {
    const key = (await generateExternalApiKey(keyName, expiry_days)).data
      .api_key;
    setApiKey(key);
    setShowApiKeyModal(true);
    queryClient.invalidateQueries(externalAPIKeys.apiKeys());
    trackEvent("User clicked on generate external api key");
    handleClose();
  };

  const handleApiKeyModalClose = () => {
    setShowApiKeyModal(false);
  };

  const healthCommand = getHealthCommand(backendURL, "<YOUR_API_KEY>");

  const columns = [
    {
      field: "display_name",
      headerName: "Key Name",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="caption"> {params.value} </Typography>
      ),
    },
    {
      field: "secret",
      headerName: "Key",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="caption">
          {`****${params.value?.slice(-8)}`}
        </Typography>
      ),
    },
    {
      field: "created_at",
      headerName: "Created",
      flex: 1,
      valueFormatter: (params) =>
        getPrettyDateTime(params?.value?.timestamp_utc, showLocalTime),
      renderCell: (params) => {
        let last_seen_display = getPrettyDateTime(
          params?.value?.timestamp_utc,
          showLocalTime
        );

        return (
          <Box style={{ whiteSpace: "nowrap" }}>
            <Typography variant="caption">{last_seen_display}</Typography>
          </Box>
        );
      },
    },
    {
      field: "owner_name",
      headerName: "Created by",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="caption"> {params.value} </Typography>
      ),
    },
    // TODO future work: https://linear.app/breeze-security/issue/DEV-2895/add-expiry-to-api-keys
    // {
    //   field: "expiry_date",
    //   headerName: "Expires",
    //   flex: 1,
    //   renderCell: (params) => (
    //     <Typography variant="caption"> {params.value} </Typography>
    //   ),
    // },
    // TODO future work: https://linear.app/breeze-security/issue/DEV-2896/add-last-used-to-api-keys
    // {
    //   field: "last_used",
    //   headerName: "Last accessed",
    //   flex: 1,
    //   renderCell: (params) => (
    //     <Typography variant="caption"> {params.value ?? "Never"} </Typography>
    //   ),
    // },
    // TODO future work: https://linear.app/breeze-security/issue/DEV-2897/support-revoke-for-api-keys
    // {
    //   field: "actions",
    //   headerName: "Revoke",
    //   flex: 1,
    //   renderCell: (params) => (
    //     <Button variant="contained" color="error">
    //       Revoke
    //     </Button>
    //   ),
    // },
  ];

  const handleViewAPIDocumentation = () => {
    window.open("/api/docs", "_blank");
  };

  return (
    <AppPage title="API Keys">
      <Box
        sx={{
          display: "flex",
          mt: "-42px",
          mb: 2,
          justifyContent: "flex-end",
          width: "100%",
        }}
      >
        <ContainedButton
          onClick={handleViewAPIDocumentation}
          color="primary"
          size="medium"
          endIcon={
            <SvgToIcon IconLogo={icons.NewTabIcon} width="20px" height="20px" />
          }
        >
          API Documentation
        </ContainedButton>
      </Box>
      <Box sx={{ width: "100%", margin: "0 auto" }}>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          gap={2}
          justifyContent="space-between"
          pb={2}
        >
          <Typography sx={{ flex: 1, maxWidth: "60%" }}>
            For security reasons, keep your API keys confidential.
          </Typography>

          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap={2}
            height="40px"
          >
            <SecondaryButton
              variant="contained"
              color={colors.primary}
              onClick={handleOpen}
            >
              Create a new API key
            </SecondaryButton>
          </Box>
          {/*  TODO future work: https://linear.app/breeze-security/issue/DEV-2897/support-revoke-for-api-keys */}
          {/* <Button variant="outlined" color="error">
              Revoke all API keys
            </Button> */}
        </Box>
        <Box sx={{ height: 400, width: "100%" }}>
          <StyledDataGrid
            rows={(apiKeys ?? []).map((key, index) => ({
              id: index,
              ...key,
            }))}
            columns={columns}
            getRowId={(row) => row.id}
            localeText={{
              noRowsLabel: "No keys",
            }}
            loading={isLoadingApiKeys}
          />
        </Box>
        <Box mt={3}>
          <Typography variant="subtitle1">Check your API key</Typography>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                p: 2,
                backgroundColor: colors.grey_200,
                borderRadius: "4px",
                display: "inline-flex",
                alignItems: "center",
                gap: 2,
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              {isLoadingBackendURL ? (
                <LoadingIcon size={20} />
              ) : (
                <>
                  <Typography
                    component="pre"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                      flex: "1 1 auto",
                      fontSize: "0.875rem",
                    }}
                  >
                    {healthCommand}
                  </Typography>
                  <CustomCopyButton
                    copyTitle="command"
                    copyText={healthCommand}
                  />
                </>
              )}
            </Box>
          </Stack>
        </Box>
      </Box>
      <ExternalAPIKeyCreationModal
        open={open}
        handleClose={handleClose}
        handleGenerateAPIKey={handleGenerateAPIKey}
      />
      <ExternalAPIKeyDisplayModal
        open={showApiKeyModal}
        handleClose={handleApiKeyModalClose}
        apiKey={apiKey}
        backendURL={backendURL}
      />
    </AppPage>
  );
};

export default ExternalAPIView;
